import { xor } from 'lodash/fp';
import { array, string } from 'fp-ts';
import { FeatureFlag } from '@mews-commander/enums';
import { CommanderThunkAction, WithAccountId, WithOrderItemId, WithScope } from '@mews-commander/types';
import { createAction } from '@mews/framework';
import { getUrlParameter, scrollIntoViewWithOptions } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import {
    fetchAccountById,
    fetchAccountingConfiguration,
    fetchAccountsByIds,
    fetchAllEnterpriseServices,
    fetchCardsByAccountId,
    fetchCurrentEnterpriseReservationsByGroupIds,
    fetchCurrentEnterpriseReservationsByReservationIds,
    fetchPreauthorizationsByAccountIds,
    fetchServiceOrderNotesByProductServiceOrderIds,
} from '@mews-commander/legacy-entities/src/actions';
import {
    deleteAllAccountBills,
    getOpenBillIdsByOwnerId,
    getReservationIdsByGroupId,
} from '@mews-commander/legacy-entities/src/ducks';
import {
    BILLING_EXTENT,
    DEFAULT_BILL_FETCH_EXTENT,
    fetchBillsForGroupBillingWithFiltering,
    fetchOpenBillsByAccountIdForAccountBilling,
} from '@mews-commander/legacy-entities/src/actions/bills';
import { getOpenBillIdsByAssociatedAccountId } from '@mews-commander/legacy-entities/src/ducks/bills';
import { fetchAccountsByBillIds } from '@mews-commander/legacy-entities/src/actions/accounts';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { getOrderItem } from '@mews-commander/legacy-entities/src/ducks/orderItems';
import { logBillingAction } from '../utils/logBillingAction';
import {
    getLinkedReservationIdsByBillIds,
    getProductServiceOrderAccountIds,
    getReservationIdsByBillIds,
} from '../ducks/reservations';
import { getProductServiceOrderIds } from '../selectors';
import { trackEvent } from '../../../middleware';
import { ACCOUNT_BILLING_DEFAULT_EXPANSION_BILL_COUNT, BILLING_EVENT_CATEGORY } from '../constants';
import {
    getAllOpenAssociatedBillIds,
    getAllOpenBillIds,
    SCROLL_TO_BILL_ANCHOR,
    SCROLL_TO_ORDER_ITEM_ANCHOR,
} from '../ducks/billing';
import {
    CHANGE_BILLING_GROUPING,
    COLLAPSE_ALL,
    EXPAND_ALL,
    INITIALIZE_BILLING,
    REPLACE_EXPANDED_BILL,
    SET_ACCOUNT_IDS,
    TOGGLE_EXPAND_BILL,
    TOGGLE_HIGHLIGHT_ORDER_ITEM,
} from '../actionTypes';
import { fetchLinkedReservationOwners } from '../utils/payments';

const setAccountIds = ({ accountIds, scope }: { accountIds: string[] } & WithScope) =>
    createAction(SET_ACCOUNT_IDS, accountIds, { scope });

export const fetchOpenServiceOrderNotesByBillIds = (billIds: $TSFixMe) => (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const state = getState();
    const enterpriseId = getCurrentEnterpriseId();
    const productServiceOrderIds = getProductServiceOrderIds(state, { billIds });

    if (productServiceOrderIds?.length) {
        return dispatch(fetchServiceOrderNotesByProductServiceOrderIds({ enterpriseId, productServiceOrderIds })).then(
            () => {
                const productServiceOrderAccountIds = getProductServiceOrderAccountIds(getState(), { billIds });
                if (productServiceOrderAccountIds?.length) {
                    return dispatch(fetchAccountsByIds({ accountIds: productServiceOrderAccountIds }));
                }
            }
        );
    }
};

export const initializeAccountBilling =
    ({ accountId, scope }: WithAccountId & WithScope): CommanderThunkAction =>
    async (dispatch, getState, { featureFlagService }) => {
        const start = window.performance.now();
        let billCount: number;
        let serviceOrderCount: number;
        const orderItemIdParam = getUrlParameter('orderItemId') as string;
        const billIdParam = getUrlParameter('billId') as string;

        dispatch(clearHighlightedOrderItem({ scope }));
        dispatch(deleteAllAccountBills(accountId));
        dispatch(setAccountIds({ accountIds: [accountId], scope }));
        const enterpriseId = getCurrentEnterpriseId();
        const accountingSettingsPromise = dispatch(fetchAccountingConfiguration(enterpriseId));
        const accountPromise = dispatch(fetchAccountById({ accountId, extent: {} }));

        const extent = { ...DEFAULT_BILL_FETCH_EXTENT, ...BILLING_EXTENT, associatedBills: true };
        const billsPromise = dispatch(fetchOpenBillsByAccountIdForAccountBilling({ accountId, extent }));
        const servicesPromise = dispatch(fetchAllEnterpriseServices({ enterpriseIds: [enterpriseId] }));

        const billsAndReservationsPromise = Promise.all([billsPromise, accountPromise]).then(async () => {
            const state = getState();
            const billIds = getOpenBillIdsByOwnerId(state, { ownerId: accountId });
            const associatedBillIds = getOpenBillIdsByAssociatedAccountId(state, { associatedAccountId: accountId });
            const serviceOrderIds = getReservationIdsByBillIds(state, { billIds });
            const linkedServiceOrderIds = getLinkedReservationIdsByBillIds(state, { billIds });
            const allServiceOrderIds = [...serviceOrderIds, ...linkedServiceOrderIds];
            billCount = billIds.length + associatedBillIds.length;
            serviceOrderCount = allServiceOrderIds.length;

            const allBillIds = [...billIds, ...associatedBillIds];

            const accountsPromise = dispatch(fetchAccountsByBillIds({ billIds: allBillIds }));
            const serviceOrderNotesPromise = dispatch(fetchOpenServiceOrderNotesByBillIds(billIds));

            const promises = [accountsPromise, serviceOrderNotesPromise];

            if (featureFlagService.hasFlag(FeatureFlag.reservationLinkToPaymentsEnabled)) {
                dispatch(fetchLinkedReservationOwners({ billIds, accountId }));
            }

            const isBillingCompany = isAccountIdCompany(getState(), { accountId });

            if (!isBillingCompany) {
                const paymentCardsPromise = dispatch(fetchCardsByAccountId({ accountId }));
                const preauthorizationsPromise = dispatch(
                    fetchPreauthorizationsByAccountIds({ accountIds: [accountId] })
                );

                promises.push(paymentCardsPromise);
                promises.push(preauthorizationsPromise);
            }

            if (serviceOrderCount) {
                const reservationsPromise = dispatch(
                    fetchCurrentEnterpriseReservationsByReservationIds({
                        reservationIds: allServiceOrderIds,
                        extent: { reservationGroups: true, accounts: true },
                    })
                );
                promises.push(reservationsPromise);
            }

            await dispatch(
                expandBillByBillIds({
                    scope,
                    billIds: [
                        ...(billCount <= ACCOUNT_BILLING_DEFAULT_EXPANSION_BILL_COUNT ? allBillIds : []),
                        ...(billIdParam ? [billIdParam] : []),
                    ],
                })
            );

            if (orderItemIdParam) {
                dispatch(setHighlightBillAndOrderItem({ orderItemId: orderItemIdParam, scope }));
            }

            return Promise.all(promises);
        });
        const promise = Promise.all([billsAndReservationsPromise, accountingSettingsPromise, servicesPromise]).then(
            () => dispatch(scrollBillingScreenOnInit())
        );

        return dispatch(createAction(INITIALIZE_BILLING, promise, { scope })).then(() => {
            dispatch(
                logBillingAction('Account billing opened', {
                    time: window.performance.now() - start,
                    accountId,
                    billCount,
                    serviceOrderCount,
                })
            );
        });
    };

export const initializeGroupBilling =
    ({ groupId, scope }: { groupId: string } & WithScope): CommanderThunkAction =>
    async (dispatch, getState) => {
        const start = window.performance.now();
        let billCount: number;
        let serviceOrderCount: number;
        const orderItemIdParam = getUrlParameter('orderItemId') as string;
        const billIdParam = getUrlParameter('billId') as string;
        const enterpriseId = getCurrentEnterpriseId();
        const accountingSettingsPromise = dispatch(fetchAccountingConfiguration(enterpriseId));
        const servicesPromise = dispatch(fetchAllEnterpriseServices({ enterpriseIds: [enterpriseId] }));

        dispatch(clearHighlightedOrderItem({ scope }));
        const billingPromise = dispatch(
            fetchCurrentEnterpriseReservationsByGroupIds({
                groupIds: [groupId],
                extent: {
                    reservationGroups: true,
                    companionships: true,
                    orderItems: true,
                },
            })
        ).then(async () => {
            const state = getState();
            const reservationIds = getReservationIdsByGroupId(state, { enterpriseId, groupId });
            return dispatch(
                fetchBillsForGroupBillingWithFiltering({
                    enterpriseId,
                    groupId,
                })
            ).then(async ({ value }) => {
                const accountIds = value.accounts?.map((it) => it.id) || [];
                dispatch(setAccountIds({ accountIds, scope }));
                const billIds = value.bills?.map((it) => it.id) || [];
                const linkedReservationsIds = getLinkedReservationIdsByBillIds(state, { billIds });
                const allReservationIds = array.uniq(string.Eq)([...reservationIds, ...linkedReservationsIds]);
                billCount = billIds.length;
                const additionalReservationIds = getReservationIdsByBillIds(getState(), { billIds });
                const reservationToLoadIds = xor(additionalReservationIds, allReservationIds);
                if (reservationToLoadIds?.length) {
                    await dispatch(
                        fetchCurrentEnterpriseReservationsByReservationIds({
                            reservationIds: reservationToLoadIds,
                            extent: { reservationGroups: true, accounts: true },
                        })
                    );
                }
                serviceOrderCount = reservationIds.length;

                const serviceOrderNotesPromise = dispatch(fetchOpenServiceOrderNotesByBillIds(billIds));

                if (orderItemIdParam) {
                    dispatch(setHighlightBillAndOrderItem({ orderItemId: orderItemIdParam, scope }));
                }

                if (billIdParam) {
                    void dispatch(expandBillByBillIds({ billIds: [billIdParam], scope }));
                } else {
                    dispatch(collapseAll(scope));
                }

                return serviceOrderNotesPromise;
            });
        });
        const promise = Promise.all([accountingSettingsPromise, billingPromise, servicesPromise]);

        return dispatch(createAction(INITIALIZE_BILLING, promise, { scope })).then(() => {
            dispatch(
                logBillingAction('Group billing opened', {
                    time: window.performance.now() - start,
                    billCount,
                    serviceOrderCount,
                })
            );
            dispatch(scrollBillingScreenOnInit());
        });
    };

const expandBillByBillIds = ({ billIds, scope }: $TSFixMe) => createAction(EXPAND_ALL, billIds, { scope });

export const expandAll =
    (scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const ownedBillIds = getAllOpenBillIds(state, { scope });
        const associatedBillIds = getAllOpenAssociatedBillIds(state, { scope });

        const billIds = [...ownedBillIds, ...associatedBillIds];

        dispatch(logBillingAction('All bills expanded'));
        dispatch(createAction(EXPAND_ALL, billIds, { scope }));
    };

export const collapseAll = (scope: $TSFixMe) => (dispatch: $TSFixMe) => {
    dispatch(logBillingAction('All bills collapsed'));
    dispatch(createAction(COLLAPSE_ALL, null, { scope }));
};

export const toggleExpandBill = ({ billId, scope }: { billId: string; scope: string }) =>
    createAction(TOGGLE_EXPAND_BILL, billId, { scope });

export const replaceExpandedBill = ({ fromBillId, toBillId, scope }: $TSFixMe) =>
    createAction(REPLACE_EXPANDED_BILL, { fromBillId, toBillId }, { scope });

export const toggleHighlightOrderItem = ({ orderItemId, scope }: WithOrderItemId & WithScope) =>
    createAction(TOGGLE_HIGHLIGHT_ORDER_ITEM, orderItemId, { scope });

export const clearHighlightedOrderItem = ({ scope }: WithScope) =>
    createAction(TOGGLE_HIGHLIGHT_ORDER_ITEM, null, { scope });

export const setHighlightBillAndOrderItem =
    ({ orderItemId, scope }: WithOrderItemId & WithScope): CommanderThunkAction =>
    (dispatch, getState) => {
        const orderItem = getOrderItem(getState(), { id: orderItemId });

        if (!orderItem) return;

        const billIdToExpand = orderItem.billId || orderItem.ownerId;

        dispatch(toggleExpandBill({ billId: billIdToExpand, scope }));
        dispatch(toggleHighlightOrderItem({ orderItemId, scope }));
    };

export const changeBillingGrouping =
    ({ grouping, scope }: $TSFixMe) =>
    (dispatch: $TSFixMe) => {
        dispatch(
            trackEvent({
                eventCategory: BILLING_EVENT_CATEGORY,
                eventAction: 'changed grouping',
                eventLabel: grouping,
            })
        );
        return dispatch(createAction(CHANGE_BILLING_GROUPING, grouping, { scope }));
    };

interface ScrollToBillProps {
    billId: string;
    orderItemId?: string;
}

const scrollToBill = ({ billId, orderItemId }: ScrollToBillProps) => {
    const billElements = document.querySelectorAll(`[${SCROLL_TO_BILL_ANCHOR}="${billId}"]`);
    const billElement = billElements[billElements.length - 1];
    const orderItemElement = document.querySelector(`[${SCROLL_TO_ORDER_ITEM_ANCHOR}="${orderItemId}"]`);

    if (orderItemElement && orderItemId) {
        scrollIntoViewWithOptions(orderItemElement, {
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
        });
    } else if (billElement) {
        scrollIntoViewWithOptions(billElement);
    }
};

const scrollBillingScreenOnInit = (): CommanderThunkAction => (dispatch, getState) => {
    const state = getState();
    const billIdParam = getUrlParameter('billId') as string;
    const orderItemIdParam = getUrlParameter('orderItemId') as string;

    if (billIdParam && !orderItemIdParam) {
        scrollToBill({ billId: billIdParam });
        return;
    }

    const orderItem = getOrderItem(state, { id: orderItemIdParam });

    if (!orderItem) return;

    if (orderItemIdParam) {
        scrollToBill({
            billId: orderItem.billId || orderItem.ownerId,
            orderItemId: orderItemIdParam,
        });
    }
};
